import {
    BarChartOutlined,
    CalendarOutlined,
    BankOutlined,
    FileTextOutlined,
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    ReloadOutlined,
    TableOutlined,
    LineChartOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    Select,
    Skeleton,
    Tabs,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Tag,
    Empty,
    DatePicker
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StatistiquesProductionStyle from './StatistiquesProductionStyle';
import {
    statistiquesGlobalesColumns,
    detailContratsColumns,
    aggregateByBranche
} from './StatistiquesProductionData';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../../variables/constants';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import Footer from '../../../../components/Footer';
import locale from 'antd/es/date-picker/locale/fr_FR';
import dayjs from 'dayjs';
import TableauxRegroupes from './TableauxRegroupes';
import EvolutionGraphiques from './EvolutionGraphiques';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;

// Le POOL voit tout et peut filtrer par organisme ; une compagnie ne voit que ses stats
// (forcé côté backend) → on masque le filtre Organisme pour elle.
const IS_POOL = LOGGED_USER?.profil?.organisme?.typeOrganisme === 'POOL';

const StatistiquesProduction = () => {
    // États pour les filtres
    const [exercices, setExercices] = useState([]);
    const [selectedExercice, setSelectedExercice] = useState(null);
    const [selectedMois, setSelectedMois] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [organismes, setOrganismes] = useState([]);
    const [selectedOrganisme, setSelectedOrganisme] = useState(null);
    const [agences, setAgences] = useState([]);
    const [selectedAgence, setSelectedAgence] = useState(null);

    // États pour les données
    const [loading, setLoading] = useState(false);
    const [bootLoading, setBootLoading] = useState(true); // chargement initial (référentiels)
    const [statistiquesGlobales, setStatistiquesGlobales] = useState([]);
    const [detailContrats, setDetailContrats] = useState([]);

    // Vue globale regroupée par BRANCHE (A/B/T → TPV ; VC/VL → VT)
    const statistiquesParBranche = useMemo(
        () => aggregateByBranche(statistiquesGlobales),
        [statistiquesGlobales]
    );

    // ── Barre de progression (dès le départ ; saute à 100 % quand les données arrivent) ──
    const busy = bootLoading || loading;
    const [progress, setProgress] = useState(0);
    const [showBar, setShowBar] = useState(true);
    useEffect(() => {
        let interval;
        if (busy) {
            setShowBar(true);
            setProgress((p) => (p < 8 ? 8 : p));
            interval = setInterval(() => {
                setProgress((p) => (p >= 92 ? 92 : p + Math.max(0.5, (92 - p) * 0.06)));
            }, 200);
            return () => clearInterval(interval);
        }
        setProgress(100);
        const t = setTimeout(() => setShowBar(false), 450);
        return () => clearTimeout(t);
    }, [busy]);

    // Onglet actif
    const [activeTab, setActiveTab] = useState("1");

    const moisOptions = [
        { value: 1, label: 'Janvier' },
        { value: 2, label: 'Février' },
        { value: 3, label: 'Mars' },
        { value: 4, label: 'Avril' },
        { value: 5, label: 'Mai' },
        { value: 6, label: 'Juin' },
        { value: 7, label: 'Juillet' },
        { value: 8, label: 'Août' },
        { value: 9, label: 'Septembre' },
        { value: 10, label: 'Octobre' },
        { value: 11, label: 'Novembre' },
        { value: 12, label: 'Décembre' }
    ];

    // Chargement initial
    useEffect(() => {
        Promise.all([
            axiosInstance.get('/api/stats/production/exercices'),
            axiosInstance.get('/api/categorie'),
            // Assurances + le POOL (permet de filtrer la production gérée par le Pool : GUM/VT…)
            axiosInstance.get('/api/organisme/all/assurances-pool'),
            axiosInstance.get('/api/agence')
        ])
            .then(([exercicesRes, catRes, orgRes, agencesRes]) => {
                setExercices(exercicesRes.data);

                // Traitement des catégories
                const excludedCodes = ["A", "B", "T"];
                const filtered = catRes.data.filter(
                    (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
                );
                const hasTPVCategories = catRes.data.some((c) => excludedCodes.includes(c.codeCategorie));
                if (hasTPVCategories) {
                    filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
                }
                setCategories(filtered);

                setOrganismes(orgRes.data);
                console.log(agencesRes.data);
                setAgences(agencesRes.data.content);

                // Par défaut : l'EXERCICE EN COURS (année courante) s'il existe, sinon le plus récent
                if (exercicesRes.data.length > 0) {
                    const currentYear = dayjs().year();
                    const enCours = exercicesRes.data.find((ex) => ex.exercice === currentYear);
                    setSelectedExercice(enCours ? enCours.exercice : exercicesRes.data[0].exercice);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
            })
            .finally(() => setBootLoading(false));
    }, []);

    // Charger les statistiques quand les filtres changent
    useEffect(() => {
        if (selectedExercice) {
            chargerStatistiques();
        }
    }, [selectedExercice]);

    const chargerStatistiques = async () => {
        setLoading(true);
        try {
            const params = {
                exercice: selectedExercice,
                mois: selectedMois || undefined,
                categorieCode: selectedCategorie || undefined,
                organismeCode: selectedOrganisme || undefined,
                agenceCode: selectedAgence || undefined
            };

            if (dateRange && dateRange.length === 2) {
                params.dateDebut = dateRange[0].format('YYYY-MM-DD');
                params.dateFin = dateRange[1].format('YYYY-MM-DD');
            }

            const [statsRes, detailRes] = await Promise.all([
                axiosInstance.get('/api/stats/production', { params }),
                axiosInstance.get('/api/stats/production/detail', { params })
            ]);

            console.log("StatsRes", statsRes.data);
            setStatistiquesGlobales(statsRes.data);
            console.log("DetailRes", detailRes.data);
            setDetailContrats(detailRes.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des statistiques");
        } finally {
            setLoading(false);
        }
    };

    // Calculs des totaux
    const calculerTotaux = () => {
        const totaux = {
            nombreContrats: 0,
            nombreEmissions: 0,
            nombreAnnulations: 0,
            montantEmissions: 0,
            montantAnnulations: 0,
            montantNet: 0
        };

        statistiquesGlobales.forEach(stat => {
            totaux.nombreContrats += stat.nombreContrats || 0;
            totaux.nombreEmissions += stat.nombreEmissions || 0;
            totaux.nombreAnnulations += stat.nombreAnnulations || 0;
            totaux.montantEmissions += stat.montantEmissions || 0;
            totaux.montantAnnulations += stat.montantAnnulations || 0;
            totaux.montantNet += stat.montantNet || 0;
        });

        return totaux;
    };

    const totaux = calculerTotaux();

    // Handlers
    const resetFilters = () => {
        setSelectedMois(null);
        setDateRange(null);
        setSelectedCategorie(null);
        setSelectedOrganisme(null);
        setSelectedAgence(null);
    };

    const handleExport = () => {
        if (!statistiquesGlobales.length && !detailContrats.length) {
            toast.info("Aucune donnée à exporter.");
            return;
        }
        try {
            const moisLabel = (m) => (m ? (moisOptions.find((o) => o.value === m)?.label || m) : 'Tous');
            const wb = XLSX.utils.book_new();

            // ── Feuille 1 : Vue globale (+ ligne TOTAL) ──────────────────────────
            const globalRows = statistiquesGlobales.map((s) => ({
                'Exercice': s.exercice,
                'Mois': moisLabel(s.mois),
                'Organisme': s.organismeNom || '-',
                'Agence': s.agenceNom || '-',
                'Catégorie': s.categorieCode || s.categorieNom || '-',
                'Contrats': s.nombreContrats || 0,
                'Assurés': s.nombreAssures || 0,
                'Émissions (Nb)': s.nombreEmissions || 0,
                'Émissions (Montant)': s.montantEmissions || 0,
                'Prime nette émissions': s.primeNetteEmissions || 0,
                'Annulations (Nb)': s.nombreAnnulations || 0,
                'Annulations (Montant)': s.montantAnnulations || 0,
                'Prime nette annulations': s.primeNetteAnnulations || 0,
                'Production nette': s.montantNet || 0,
                'Prime nette': s.primeNetteNet || 0,
            }));
            globalRows.push({
                'Exercice': 'TOTAL', 'Mois': '', 'Organisme': '', 'Agence': '', 'Catégorie': '',
                'Contrats': totaux.nombreContrats,
                'Assurés': '',
                'Émissions (Nb)': totaux.nombreEmissions,
                'Émissions (Montant)': totaux.montantEmissions,
                'Prime nette émissions': '',
                'Annulations (Nb)': totaux.nombreAnnulations,
                'Annulations (Montant)': totaux.montantAnnulations,
                'Prime nette annulations': '',
                'Production nette': totaux.montantNet,
                'Prime nette': '',
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(globalRows), 'Vue globale');

            // ── Feuille 2 : Détail des contrats ──────────────────────────────────
            if (detailContrats.length) {
                const detailRows = detailContrats.map((d) => ({
                    'N° Police': d.numeroPolice || '-',
                    'Assuré': d.assureNom || '-',
                    'Date Effet': d.dateEffet ? new Date(d.dateEffet).toLocaleDateString('fr-FR') : '-',
                    'Organisme': d.organismeNom || '-',
                    'Agence': d.agenceNom || '-',
                    'Catégorie': d.categorieCode || '-',
                    'Producteur': d.producteurNom || '-',
                    'Émissions (Nb)': d.nombreEmissions || 0,
                    'Émissions (Montant)': d.montantEmission || 0,
                    'Annulations (Nb)': d.nombreAnnulations || 0,
                    'Annulations (Montant)': d.montantAnnulation || 0,
                    'Montant Net': d.montantNet || 0,
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Détail contrats');
            }

            const orgLabel = selectedOrganisme
                ? (organismes.find((o) => o.code === selectedOrganisme)?.raisonSociale || selectedOrganisme)
                : 'Tous';
            XLSX.writeFile(wb, `Statistiques_production_${selectedExercice || ''}_${orgLabel}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de l'export Excel.");
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <StatistiquesProductionStyle />

            {/* Barre de progression fine en haut de page (dès le chargement initial) */}
            {showBar && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 3000, background: 'transparent' }}>
                    <div style={{
                        height: '100%', width: `${progress}%`, background: '#242166',
                        boxShadow: '0 0 8px rgba(36,33,102,0.6)', transition: 'width .2s ease',
                    }} />
                </div>
            )}

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f0f2f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* Header */}
                            <div className="production-page-header">
                                <div className="header-content">
                                    <div>
                                        <h1>
                                            <LineChartOutlined /> Statistiques de Production
                                        </h1>
                                        <p>
                                            États statistiques des émissions et annulations par agence et compagnie
                                        </p>
                                    </div>
                                    <Space>
                                        <Button icon={<DownloadOutlined />} onClick={handleExport}>
                                            Exporter Excel
                                        </Button>
                                        <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                                            Imprimer
                                        </Button>
                                    </Space>
                                </div>
                            </div>

                            {/* Filtres */}
                            <Card className="filters-card" style={{ marginBottom: 24 }}>
                                <Row gutter={[16, 16]} align="middle">
                                    <Col xs={24} sm={12} md={6} lg={4}>
                                        <div className="filter-item">
                                            <label><CalendarOutlined /> Exercice</label>
                                            <Select
                                                placeholder="Exercice"
                                                style={{ width: '100%' }}
                                                value={selectedExercice}
                                                onChange={setSelectedExercice}
                                                options={exercices.map(ex => ({
                                                    value: ex.exercice,
                                                    label: (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{ex.exercice}</span>
                                                            <Tag color="blue">{ex.nombreContrats}</Tag>
                                                        </div>
                                                    )
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6} lg={4}>
                                        <div className="filter-item">
                                            <label>Mois</label>
                                            <Select
                                                placeholder="Tous les mois"
                                                style={{ width: '100%' }}
                                                value={selectedMois}
                                                onChange={setSelectedMois}
                                                allowClear
                                                options={moisOptions}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6}>
                                        <div className="filter-item">
                                            <label>Période personnalisée</label>
                                            <RangePicker
                                                style={{ width: '100%' }}
                                                locale={locale}
                                                value={dateRange}
                                                onChange={setDateRange}
                                                format="DD/MM/YYYY"
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6} lg={5} style={IS_POOL ? {} : { display: 'none' }}>
                                        <div className="filter-item">
                                            <label><BankOutlined /> Organisme</label>
                                            <Select
                                                placeholder="Tous"
                                                style={{ width: '100%' }}
                                                value={selectedOrganisme}
                                                onChange={setSelectedOrganisme}
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                options={organismes.map(org => ({
                                                    value: org.code,
                                                    label: org.raisonSociale
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6} lg={5}>
                                        <div className="filter-item">
                                            <label>Agence</label>
                                            <Select
                                                placeholder="Toutes"
                                                style={{ width: '100%' }}
                                                value={selectedAgence}
                                                onChange={setSelectedAgence}
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                options={agences && agences?.map(ag => ({
                                                    value: ag.codeAgence,
                                                    label: ag.nom
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={24} sm={12} md={6} lg={5}>
                                        <div className="filter-item">
                                            <label><FileTextOutlined /> Catégorie</label>
                                            <Select
                                                placeholder="Toutes"
                                                style={{ width: '100%' }}
                                                value={selectedCategorie}
                                                onChange={setSelectedCategorie}
                                                allowClear
                                                options={categories.map(cat => ({
                                                    value: cat.codeCategorie,
                                                    label: cat.nom
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Space style={{ marginTop: 22 }}>
                                            <Button
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={chargerStatistiques}
                                                loading={loading}
                                            >
                                                Rechercher
                                            </Button>
                                            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                                                Réinitialiser
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Cartes de résumé */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Nombre de contrats"
                                            value={totaux.nombreContrats}
                                            prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Émissions"
                                            value={displayNumbers(totaux.montantEmissions)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#52c41a' }} />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                            {totaux.nombreEmissions} opération(s)
                                        </div>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Annulations"
                                            value={displayNumbers(totaux.montantAnnulations)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#ff4d4f' }} />}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                            {totaux.nombreAnnulations} opération(s)
                                        </div>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card stat-card-highlight">
                                        <Statistic
                                            title="Production Nette"
                                            value={displayNumbers(totaux.montantNet)}
                                            suffix="F"
                                            prefix={<LineChartOutlined style={{ color: '#722ed1' }} />}
                                            valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                            Émissions - Annulations
                                        </div>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Onglets principaux */}
                            <Card>
                                <ConfigProvider locale={frFR}>
                                    <Skeleton loading={loading} active>
                                        <Tabs
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            items={[
                                                {
                                                    label: (
                                                        <span>
                                                            <TableOutlined /> Vue globale
                                                        </span>
                                                    ),
                                                    key: "1",
                                                    children: (
                                                        <Table
                                                            columns={statistiquesGlobalesColumns()}
                                                            dataSource={statistiquesParBranche}
                                                            rowKey={(record) =>
                                                                `${record.exercice}-${record.mois || 'all'}-${record.organismeCode}-${record.agenceCode}-${record.categorieCode}`
                                                            }
                                                            pagination={{
                                                                pageSize: 20,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} ligne(s)`
                                                            }}
                                                            scroll={{ x: 'max-content' }}
                                                            bordered
                                                            size="small"
                                                            locale={{
                                                                emptyText: <Empty description="Aucune donnée disponible" />
                                                            }}
                                                            expandable={{
                                                                expandedRowRender: (record) => {
                                                                    if (!record.detailParProducteur || record.detailParProducteur.length === 0) {
                                                                        return <Empty description="Aucun détail producteur" />;
                                                                    }
                                                                    return (
                                                                        <div style={{ padding: 16, background: '#fafafa' }}>
                                                                            <h4>Détail par producteur:</h4>
                                                                            <Row gutter={[16, 16]}>
                                                                                {record.detailParProducteur.map((prod, idx) => (
                                                                                    <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                                                                                        <Card size="small">
                                                                                            <div><strong>{prod.producteurNom}</strong></div>
                                                                                            <div>Contrats: {prod.nombreContrats}</div>
                                                                                            <div style={{ color: '#52c41a' }}>
                                                                                                Émissions: {displayNumbers(prod.montantEmissions)} F
                                                                                            </div>
                                                                                            <div style={{ color: '#ff4d4f' }}>
                                                                                                Annulations: {displayNumbers(prod.montantAnnulations)} F
                                                                                            </div>
                                                                                            <div style={{ fontWeight: 600, color: '#1890ff' }}>
                                                                                                Net: {displayNumbers(prod.montantNet)} F
                                                                                            </div>
                                                                                        </Card>
                                                                                    </Col>
                                                                                ))}
                                                                            </Row>
                                                                        </div>
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <FileTextOutlined /> Détail des contrats
                                                        </span>
                                                    ),
                                                    key: "2",
                                                    children: (
                                                        <Table
                                                            columns={detailContratsColumns()}
                                                            dataSource={detailContrats.filter((d) => d.numeroPolice && String(d.numeroPolice).trim() !== "")}
                                                            rowKey="contratCodeId"
                                                            pagination={{
                                                                pageSize: 20,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} contrat(s)`
                                                            }}
                                                            scroll={{ x: 'max-content' }}
                                                            bordered
                                                            size="small"
                                                            locale={{
                                                                emptyText: <Empty description="Aucun contrat disponible" />
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <LineChartOutlined /> Évolution temporelle
                                                        </span>
                                                    ),
                                                    key: "3",
                                                    children: (
                                                        <EvolutionGraphiques
                                                            data={statistiquesGlobales}
                                                            exercice={selectedExercice}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <BankOutlined /> Tableaux regroupés
                                                        </span>
                                                    ),
                                                    key: "4",
                                                    children: (
                                                        <TableauxRegroupes
                                                            sourceData={statistiquesGlobales}
                                                            loading={loading}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Skeleton>
                                </ConfigProvider>
                            </Card>
                        </div>

                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatistiquesProduction;